import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Calendar, CheckCircle, XCircle, Loader } from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';

const MicrosoftCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState('processing'); // 'processing', 'success', 'error'
  const [message, setMessage] = useState('Processing Microsoft OAuth callback...');
  const hasProcessed = useRef(false); // Prevent duplicate processing
  const timeoutsRef = useRef([]); // Store timeout IDs for cleanup

  useEffect(() => {
    // Only process if we haven't already processed this callback
    if (!hasProcessed.current) {
      hasProcessed.current = true;
      handleCallback();
    }

    // Cleanup function to handle component unmounting
    return () => {
      // Clear all pending timeouts
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, []);

  const addTimeout = (callback, delay) => {
    const timeoutId = setTimeout(callback, delay);
    timeoutsRef.current.push(timeoutId);
    return timeoutId;
  };

  const handleCallback = async () => {
    try {
      // Get the authorization code from URL parameters
      const urlParams = new URLSearchParams(location.search);
      const code = urlParams.get('code');
      const error = urlParams.get('error');
      const errorDescription = urlParams.get('error_description');


      if (error) {
        console.error('Microsoft OAuth error:', error, errorDescription);
        setStatus('error');
        setMessage(`OAuth Error: ${errorDescription || error}`);
        toast.error(`Failed to connect Microsoft Calendar: ${errorDescription || error}`);
        
        // Check if user came from onboarding
        const fromOnboarding = sessionStorage.getItem('from_onboarding');
        
        // Redirect based on where user came from
        addTimeout(() => {
          if (fromOnboarding) {
            // Clear the flag and redirect back to onboarding
            sessionStorage.removeItem('from_onboarding');
            navigate('/onboarding');
          } else {
            // Default redirect to profile
            navigate('/profile?tab=integrations');
          }
        }, 3000);
        return;
      }

      if (!code) {
        setStatus('error');
        setMessage('No authorization code received from Microsoft');
        toast.error('No authorization code received from Microsoft');
        
        // Check if user came from onboarding
        const fromOnboarding = sessionStorage.getItem('from_onboarding');
        
        // Redirect based on where user came from
        addTimeout(() => {
          if (fromOnboarding) {
            // Clear the flag and redirect back to onboarding
            sessionStorage.removeItem('from_onboarding');
            navigate('/onboarding');
          } else {
            // Default redirect to profile
            navigate('/profile?tab=integrations');
          }
        }, 3000);
        return;
      }

      // Check if this code has already been processed in this session
      const processedCode = sessionStorage.getItem('processed_oauth_code');
      if (processedCode === code) {
        setStatus('error');
        setMessage('This authorization code has already been processed. Please try connecting again.');
        toast.error('Authorization code already processed. Please try connecting again.');
        
        // Check if user came from onboarding
        const fromOnboarding = sessionStorage.getItem('from_onboarding');
        
        // Redirect based on where user came from
        addTimeout(() => {
          if (fromOnboarding) {
            // Clear the flag and redirect back to onboarding
            sessionStorage.removeItem('from_onboarding');
            navigate('/onboarding');
          } else {
            // Default redirect to profile
            navigate('/profile?tab=integrations');
          }
        }, 3000);
        return;
      }

      // Mark this code as being processed
      sessionStorage.setItem('processed_oauth_code', code);

      // Exchange the authorization code for access token
      try {
        // Check if user is authenticated
        const token = localStorage.getItem('token');
      
        
        if (!token) {
          console.error('No authentication token found in localStorage');
          throw new Error('Authentication token not found. Please log in again.');
        }
        
        
        // Set the Authorization header manually since we're on a different page
        const response = await axios.post('/api/calendar/connect', {
          code: code,
          redirect_uri: `${window.location.origin}/auth/microsoft/callback`
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.data.success) {
          setStatus('success');
          setMessage('Microsoft Calendar connected successfully!');
          toast.success('Microsoft Calendar connected successfully!');
          
          // Update the user's Microsoft calendar connection status in the backend
          try {
            await axios.post('/api/users/update-microsoft-calendar-status', {
              connected: true
            });
          } catch (updateError) {
            console.warn('Failed to update Microsoft calendar connection status:', updateError);
            // Don't fail the connection if this update fails
          }
          
          // Set flag in session storage to indicate successful connection
          sessionStorage.setItem('microsoft_calendar_connected', 'true');
          
          // Clear the processed code flag
          sessionStorage.removeItem('processed_oauth_code');
          
          // Check if user came from onboarding
          const fromOnboarding = sessionStorage.getItem('from_onboarding');
          
          // Redirect based on where user came from
          addTimeout(() => {
            if (fromOnboarding) {
              // Clear the flag and redirect back to onboarding
              sessionStorage.removeItem('from_onboarding');
              navigate('/onboarding');
            } else {
              // Default redirect to profile
              navigate('/profile?tab=integrations');
            }
          }, 3000);
        } else {
          throw new Error(response.data.message || 'Failed to connect calendar');
        }
      } catch (error) {
        console.error('Error connecting Microsoft Calendar:', error);
        
        // Clear the processed code flag on error
        sessionStorage.removeItem('processed_oauth_code');
        
        // Check if the error is due to already redeemed code
        if (error.response?.data?.detail && 
            error.response.data.detail.includes('already redeemed')) {
          setStatus('error');
          setMessage('This authorization code has already been used. Please try connecting again.');
          toast.error('Authorization code already used. Please try connecting again.');
        } else {
          setStatus('error');
          setMessage(`Failed to connect calendar: ${error.response?.data?.detail || error.message}`);
          toast.error(`Failed to connect Microsoft Calendar: ${error.response?.data?.detail || error.message}`);
        }
        
        // Check if user came from onboarding
        const fromOnboarding = sessionStorage.getItem('from_onboarding');
        
        // Redirect based on where user came from
        addTimeout(() => {
          if (fromOnboarding) {
            // Clear the flag and redirect back to onboarding
            sessionStorage.removeItem('from_onboarding');
            navigate('/onboarding');
          } else {
            // Default redirect to profile
            navigate('/profile?tab=integrations');
          }
        }, 3000);
      }
    } catch (error) {
      console.error('Unexpected error in Microsoft callback:', error);
      setStatus('error');
      setMessage('An unexpected error occurred');
      toast.error('An unexpected error occurred');
      
      // Clear the processed code flag on error
      sessionStorage.removeItem('processed_oauth_code');
      
      // Check if user came from onboarding
      const fromOnboarding = sessionStorage.getItem('from_onboarding');
      
      // Redirect based on where user came from
      addTimeout(() => {
        if (fromOnboarding) {
          // Clear the flag and redirect back to onboarding
          sessionStorage.removeItem('from_onboarding');
          navigate('/onboarding');
        } else {
          // Default redirect to profile
          navigate('/profile?tab=integrations');
        }
      }, 3000);
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'processing':
        return <Loader className="h-12 w-12 text-blue-600 animate-spin" />;
      case 'success':
        return <CheckCircle className="h-12 w-12 text-green-600" />;
      case 'error':
        return <XCircle className="h-12 w-12 text-red-600" />;
      default:
        return <Calendar className="h-12 w-12 text-gray-600" />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'processing':
        return 'text-blue-600';
      case 'success':
        return 'text-green-600';
      case 'error':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <div className="auth-page min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md w-full mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="mb-6">
            {getStatusIcon()}
          </div>
          
          <h1 className={`text-2xl font-bold mb-4 ${getStatusColor()}`}>
            Microsoft Calendar Integration
          </h1>
          
          <p className="text-gray-600 mb-6">
            {message}
          </p>
          
          {status === 'processing' && (
            <div className="space-y-3">
              <div className="flex items-center justify-center space-x-2">
                <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              </div>
              <p className="text-sm text-gray-500">
                Please wait while we complete the connection...
              </p>
            </div>
          )}
          
          {status !== 'processing' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">
                {sessionStorage.getItem('from_onboarding') 
                  ? 'You will be redirected back to onboarding in a few seconds.'
                  : 'You will be redirected back to your profile in a few seconds.'
                }
              </p>
              <button
                onClick={() => {
                  const fromOnboarding = sessionStorage.getItem('from_onboarding');
                  if (fromOnboarding) {
                    sessionStorage.removeItem('from_onboarding');
                    navigate('/onboarding');
                  } else {
                    navigate('/profile?tab=integrations');
                  }
                }}
                className="w-full bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700 transition-colors"
              >
                {sessionStorage.getItem('from_onboarding') ? 'Go to Onboarding Now' : 'Go to Profile Now'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MicrosoftCallback;
